import { Fragment, useContext, useEffectEvent, useId, useLayoutEffect, useRef } from "react";
import { LogoWaits, playLogo, type LogoClock } from "./clock";
import {
  dashOf,
  inMark,
  inWord,
  LETTER_NAMES,
  LETTERS,
  MARK_LETTERS,
  stageOf,
  type Letter,
  type LogoLayout,
  type Stroke,
} from "./drawing";
import { logoTimeline } from "./timeline";

interface Props {
  /** Where the mark ends: in the middle of the word, or at its start (the left of a bar). */
  layout?: LogoLayout;
  /** In the colour of the text around it (white on the red head of the Tafo app) instead of the reds of the logo. */
  white?: boolean;
  /** Written the moment it comes on the screen (the opening of the till), or with the page (a logo at the top). */
  clock?: LogoClock;
  /** The mark stands and the animation is over; at once where the logo is not animated. */
  onEnd?: () => void;
}

/** A place on the stage to the hundredth of a pixel of the logo. */
function fixed(value: number): number {
  return Math.round(value * 100) / 100;
}

/** The slanted space the foot of the A is written in; `back` leads out of it again. */
function slanted(skew: NonNullable<Stroke["skew"]>, back = false): string {
  return `translate(0 ${skew.around}) skewX(${back ? -skew.degrees : skew.degrees}) translate(0 ${-skew.around})`;
}

function Gradient({ id, letter, transform }: { id: string; letter: Letter; transform?: string }) {
  const { x1, y1, x2, y2, stops } = letter.gradient;
  return (
    <linearGradient
      id={id}
      gradientUnits="userSpaceOnUse"
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      gradientTransform={transform}
    >
      {stops.map((colour, index) => (
        <stop key={`${String(index)} ${colour}`} offset={index / (stops.length - 1)} stopColor={colour} />
      ))}
    </linearGradient>
  );
}

/**
 * The logo of Tafo, written (Furkan, 02/10/2026; ADR 0015, 0016): "TAFO" letter by letter, then the T and
 * the F are taken back and the A and the O turn into the mark, the A above the O. The drawing itself is
 * the mark: the animation lays the word over it for as long as it lasts, so whatever cannot animate shows
 * the mark, and when the animation is over nothing of it is left.
 */
export function LogoAnimation({ layout = "middle", white = false, clock = "own", onEnd }: Props) {
  const id = useId();
  const drawing = useRef<SVGSVGElement>(null);
  const waits = useContext(LogoWaits) && clock === "page";
  const ended = useEffectEvent(() => {
    onEnd?.();
  });

  // before the screen is drawn: the mark is never seen and then taken away for the word
  useLayoutEffect(() => {
    const svg = drawing.current;
    if (svg === null || waits) return;
    const animations = playLogo(svg, logoTimeline(layout), clock);
    let left = false;
    void Promise.all(animations.map((animation) => animation.finished)).then(
      () => {
        if (!left) ended();
      },
      () => undefined, // cancelled: the logo left the screen before the end
    );
    return () => {
      left = true;
      for (const animation of animations) animation.cancel();
    };
  }, [layout, clock, waits]);

  const stage = stageOf(layout);
  /** A gradient or a clip path of this logo: every logo on a page has its own. */
  const own = (name: string) => `url(#${id}-${name})`;
  /** The paint of a letter, or of one of its lines. */
  const paint = (name: string) => (white ? "currentColor" : own(`red-${name}`));
  return (
    <svg
      ref={drawing}
      className="logo-animation"
      viewBox={`0 0 ${fixed(stage.width)} ${fixed(stage.height)}`}
      role="img"
      aria-label="Tafo"
    >
      <defs>
        {LETTER_NAMES.map((name) => {
          const letter = LETTERS[name];
          return (
            <Fragment key={name}>
              {!white && <Gradient id={`${id}-red-${name}`} letter={letter} />}
              <clipPath id={`${id}-letter-${name}`}>
                <path clipRule="evenodd" d={letter.outline} />
              </clipPath>
              {letter.strokes.map((stroke) => (
                <Fragment key={stroke.key}>
                  {stroke.within !== undefined && (
                    <clipPath id={`${id}-within-${stroke.key}`}>
                      <path d={stroke.within} />
                    </clipPath>
                  )}
                  {/* the same gradient, seen from the slanted space */}
                  {!white && stroke.skew !== undefined && (
                    <Gradient id={`${id}-red-${stroke.key}`} letter={letter} transform={slanted(stroke.skew, true)} />
                  )}
                </Fragment>
              ))}
            </Fragment>
          );
        })}
      </defs>

      {/* the word: only there while it is animated. Each letter as the lines that write it, cut to its
          outline (a clip path and not a mask: with a mask Chromium draws the whole logo softer), and as its
          plain outline once it is written. */}
      <g visibility="hidden">
        {LETTER_NAMES.map((name) => {
          const letter = LETTERS[name];
          const place = inWord(name, layout);
          return (
            <g key={name} transform={`translate(${fixed(place.x)} ${fixed(place.y)})`}>
              <g data-part={`lines-${name}`} clipPath={own(`letter-${name}`)}>
                {letter.strokes.map((stroke) => {
                  const dash = dashOf(stroke);
                  let line = (
                    <path
                      data-part={stroke.key}
                      d={stroke.path}
                      fill="none"
                      stroke={paint(stroke.skew === undefined ? name : stroke.key)}
                      strokeWidth={stroke.width}
                      strokeLinejoin={stroke.round ? "round" : undefined}
                      strokeDasharray={dash}
                      strokeDashoffset={dash}
                    />
                  );
                  if (stroke.skew !== undefined) line = <g transform={slanted(stroke.skew)}>{line}</g>;
                  if (stroke.within !== undefined) line = <g clipPath={own(`within-${stroke.key}`)}>{line}</g>;
                  return <Fragment key={stroke.key}>{line}</Fragment>;
                })}
              </g>
              <path data-part={`outline-${name}`} d={letter.outline} fill={paint(name)} fillRule="evenodd" />
            </g>
          );
        })}
      </g>

      {/* the mark: a second A and O, where they stand in the mark. Only they are moved, and only during the
          turn: a browser draws what it moves a little softer, and the word and the mark must stand sharp. */}
      {MARK_LETTERS.map((name) => {
        const place = inMark(name, layout);
        return (
          <g key={name} transform={`translate(${fixed(place.x)} ${fixed(place.y)})`}>
            <path data-part={`mark-${name}`} d={LETTERS[name].outline} fill={paint(name)} fillRule="evenodd" />
          </g>
        );
      })}
    </svg>
  );
}
